import React, { type PropsWithChildren, type RefObject, useEffect, useRef } from "react"
import { createPortal } from "react-dom"
import clsx from "classnames"

interface LightboxProps {
  className?: string
  labelledBy: string
  onClose: () => void
  // To close from the content: dialogRef.current.close().
  dialogRef?: RefObject<HTMLDialogElement>
}

// A native modal <dialog> in the legacy lightbox style (matching the backbone version for now)
export const Lightbox = ({
  children,
  className,
  labelledBy,
  onClose,
  dialogRef,
}: PropsWithChildren<LightboxProps>) => {
  "use memo"

  const ownRef = useRef<HTMLDialogElement>(null)
  const ref = dialogRef ?? ownRef
  const pressStartedOnBackdrop = useRef(false)

  useEffect(() => {
    const dialog = ref.current
    if (!dialog.open) dialog.showModal()
    document.getElementById(labelledBy)?.focus()
    document.body.classList.add("overflow-hidden")

    return () => document.body.classList.remove("overflow-hidden")
  }, [ref, labelledBy])

  // Close only on a primary-button press that starts and ends on the backdrop. Not
  // click: a drag between content and backdrop clicks the backdrop itself.
  const isPrimaryPressOnBackdrop = (event: React.MouseEvent<HTMLDivElement>) =>
    event.button === 0 && event.target === event.currentTarget

  const handleMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    pressStartedOnBackdrop.current = isPrimaryPressOnBackdrop(event)
  }

  const handleMouseUp = (event: React.MouseEvent<HTMLDivElement>) => {
    if (pressStartedOnBackdrop.current && isPrimaryPressOnBackdrop(event)) ref.current.close()
    pressStartedOnBackdrop.current = false
  }

  return createPortal(
    <dialog
      ref={ref}
      className={clsx(
        className,
        "h-full max-h-none w-full max-w-none border-none bg-black/50 p-0 backdrop:bg-transparent",
      )}
      aria-labelledby={labelledBy}
      onClose={onClose}
    >
      <div
        role="presentation"
        className="box-border min-h-full pt-25 max-md:px-2.5"
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
      >
        <div
          className="mx-auto w-200 border border-gray-200 bg-white shadow-md transition duration-250 ease-out max-md:w-full starting:scale-0 starting:opacity-0"
          role="document"
        >
          {children}
        </div>
      </div>
    </dialog>,
    document.body,
  )
}
